import { call, put, takeLatest, select } from "redux-saga/effects";
import React from "react";
import * as types from "store/interviewsList/interviewsListTypes";
import API from "store/interviewsList/interviewsListApis";
import { propEq, find, ascend, descend, prop, path } from "ramda";
import { isPMOrAdmin } from "shared/helpers/user";

const sortInterviews = (currentTabValue, interviews) => {
  if (currentTabValue === "invalidate") {
    return interviews.sort(descend(prop("updatedOn")));
  }
  const sortDir = currentTabValue === "upcoming" ? ascend : descend;
  return interviews.sort(sortDir(prop("endTime")));
};

const getActiveTab = (tabs) => find(propEq("active", true))(tabs).value;

function* getInterviewsList({ payload: inPayload }) {
  try {
    let offset = 0;
    let handleScroll = false;
    if (inPayload && inPayload.offset) {
      offset = inPayload.offset;
      handleScroll = true;
    }
    const isPM = isPMOrAdmin();
    const clientId = yield select(path(["client", "clientId"]));
    const tabs = yield select(path(["interviewsList", "tabs"]));
    const moderatorId = yield select(path(["user", "userId"]));
    const activeTab = getActiveTab(tabs);
    const q = yield select(path(["interviewsList", "q"]));
    const filteredItems = yield select(
      path(["interviewsList", "filteredItems"]),
    );
    const filteredClients = yield select(path(["user", "filteredClients"]));
    const paymentStatusCode = yield select(
      path(["interviewsList", "paymentStatusCode"]),
    );
    let payload = {
      isProjectManager: isPM,
      clientId,
      activeTab,
      moderatorId,
      q,
      filteredItems,
      filteredClients,
      offset,
      handleScroll,
    };
    if (paymentStatusCode !== "ALL")
      payload.paymentStatusCode = paymentStatusCode;
    let response = {};
    let responsePayload = {};
    payload.filteredClients = [...payload.filteredClients];
    if (payload.filteredItems)
      payload.filteredItems = [...payload.filteredItems];
    if (payload.isProjectManager) {
      response = yield call(API.getInterviewsListForPM, payload);
      responsePayload = response.data.data.records;
    } else {
      response = yield call(API.getInterviewsListForMod, payload);
      responsePayload = response.data;
    }
    if (response.status === 200) {
      yield put({
        type: "FETCH_INTERVIEWS_LIST_SUCCEEDED",
        payload: responsePayload,
      });
      const formattedInterviews = sortInterviews(
        payload.activeTab,
        responsePayload.map((i) =>
          Object.assign(i, {
            startTime: new Date(i.startTime),
            endTime: new Date(i.endTime),
            updatedOn: i.updatedOn && new Date(i.updatedOn),
          }),
        ),
      );
      yield put({
        type: "SET_FORMATTED_INTERVIEWS",
        payload: formattedInterviews,
      });
    } else if (response.status === 401) {
      yield put({ type: "UNAUTHORIZED" });
    } else {
      yield put({ type: "FETCH_INTERVIEWS_LIST_FAILED" });
      yield put({
        type: "SHOW_MESSAGE",
        payload: {
          message:
            "There was an error processing your request, please try again",
          className: "copy-screeners-snackbar",
        },
      });
      yield put({ type: "HIDE_MESSAGE" });
    }
  } catch (err) {
    yield put({ type: "FETCH_INTERVIEWS_LIST_FAILED", err });
    yield put({
      type: "SHOW_MESSAGE",
      payload: {
        message: "There was an error processing your request, please try again",
        className: "copy-screeners-snackbar",
      },
    });
    yield put({ type: "HIDE_MESSAGE" });
  }
}
export default function* interviewsListSaga() {
  yield takeLatest(types.FETCH_INTERVIEWS_LIST_REQUESTED, getInterviewsList);
  yield takeLatest(types.INVALIDATE_INTERVIEW_REQUESTED, invalidateInterview);
}

function* invalidateInterview({ payload }) {
  try {
    const userId = yield select(path(["user", "userId"]));
    const tabs = yield select(path(["interviewsList", "tabs"]));
    const reasonMap = {
      participant_no_show: "PARTICIPANT_NO_SHOW",
      moderator_no_show: "MODERATOR_NO_SHOW",
      other: "OTHER",
    };
    const code =
      reasonMap[payload.reason] || (payload.reason || "").toUpperCase();
    const body = {
      timeSlotId: payload.timeSlotId,
      invalidationReasonCode: code,
      invalidatedByUserId: userId,
      isInvalidateEmailSent: Boolean(payload.isInvalidateEmailSent) || false,
    };
    if (
      code === "OTHER" &&
      payload.otherReason &&
      String(payload.otherReason).trim().length > 0
    ) {
      body.invalidationReasonText = String(payload.otherReason).trim();
    }
    const response = yield call(API.invalidateInterview, body);
    if (response.status === 200) {
      yield put({ type: types.INVALIDATE_INTERVIEW_SUCCEEDED });
      // Switch tab to Invalidated and refetch
      const updatedTabs = tabs.map((t) => ({
        ...t,
        active: t.value === "invalidate",
      }));
      yield put({ type: types.SET_TABS, payload: updatedTabs });
      yield put({ type: types.FETCH_INTERVIEWS_LIST_REQUESTED });
      if (payload && payload.isInvalidateEmailSent) {
        yield put({
          type: "SHOW_MESSAGE",
          payload: {
            message: (
              <span>
                <strong>Reschedule emails have been sent.</strong> The
                participant will have the option to choose another time.
                Previous interview has been invalidated.
              </span>
            ),
            className: "copy-screeners-snackbar",
          },
        });
        yield put({ type: "HIDE_MESSAGE" });
      } else if (!(payload && payload.isInvalidateEmailSent)) {
        yield put({
          type: "SHOW_MESSAGE",
          payload: {
            message:
              payload && payload.projectName && payload.externalSurveyId ? (
                <span>
                  <strong>
                    {payload.projectName} #{payload.externalSurveyId}
                  </strong>{" "}
                  has been invalidated.
                </span>
              ) : (
                "Interview invalidated"
              ),
            className: "copy-screeners-snackbar",
          },
        });
        yield put({ type: "HIDE_MESSAGE" });
      }
      // Close modal if a success callback is provided
      if (payload && typeof payload.onSave === "function") {
        yield payload.onSave();
      }
    } else {
      yield put({ type: types.INVALIDATE_INTERVIEW_FAILED });
      yield put({
        type: "SHOW_MESSAGE",
        payload: {
          message: "Failed to invalidate interview",
          className: "copy-screeners-snackbar",
        },
      });
      yield put({ type: "HIDE_MESSAGE" });
    }
  } catch (err) {
    yield put({ type: types.INVALIDATE_INTERVIEW_FAILED, err });
    yield put({
      type: "SHOW_MESSAGE",
      payload: {
        message: "There was an error processing your request, please try again",
        className: "copy-screeners-snackbar",
      },
    });
    yield put({ type: "HIDE_MESSAGE" });
  }
}
